import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, Plane, Stamp, Clock, MapPin } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import { getCountry, COUNTRIES } from "@/data/nri";
import { getCondition } from "@/data/care";
import { WHATSAPP_LINK } from "@/lib/api";

const LOGISTIC_ICONS = {
  "Direct flights": Plane,
  "Ideal stay": Clock,
  "Medical visa": Stamp,
  "Best months": Clock,
  "Insurance letters": Stamp,
  "GP letters": Stamp,
  "Employer letters": Stamp,
  "Language & culture": MapPin,
};

export default function Country() {
  const { country: slug } = useParams();
  const c = getCountry(slug);
  if (!c) return <Navigate to="/nri" replace />;

  const others = COUNTRIES.filter((x) => x.slug !== c.slug);
  const commonConds = (c.commonConditions || []).map(getCondition).filter(Boolean);

  return (
    <div data-testid={`country-page-${c.slug}`} className="pt-28 md:pt-36 pb-28">
      <SEO
        title={`${c.name} — NRI Vascular & Endovascular IR · Dr Tegbir Vascular, Mohali`}
        description={`Video-first vascular care for patients travelling from ${c.name} to Mohali. ${c.lede}`}
        keywords={[`vascular treatment ${c.name} to india`, `NRI IR ${c.name} mohali`, `medical visa ${c.name} india`]}
      />
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { to: "/nri", label: "For NRI" }, { label: c.name }]} />
        </FadeUp>

        {/* Hero */}
        <div className="mt-12 grid grid-cols-12 gap-6">
          <FadeUp className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-4">
              <span className="text-6xl" aria-hidden>{c.flag}</span>
              <div className="kbd">{c.currency} · {c.name}</div>
            </div>
            <h1 className="mt-8 font-serif text-6xl md:text-[7.5vw] leading-[0.96] tracking-tightest">
              <MaskedReveal text={`From ${c.name},`} stagger={0.05}/>
              <MaskedReveal text={"to Mohali,"} delay={0.15}/>
              <MaskedReveal text={"and back."} delay={0.28} className="italic font-normal"/>
            </h1>
            <FadeUp className="mt-10" delay={0.4}>
              <p className="text-xl md:text-2xl font-serif italic max-w-2xl leading-snug">"{c.lede}"</p>
              <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-2xl">{c.whyTravel}</p>
            </FadeUp>
          </FadeUp>

          <FadeUp className="col-span-12 md:col-span-4 md:pl-8" delay={0.2}>
            <div className="hairline bg-white/70 p-6">
              <div className="kbd">Start here</div>
              <a target="_blank" rel="noreferrer" href={WHATSAPP_LINK(`Hello Dr Sidhu, I'm writing from ${c.name}. I would like to enquire about a consultation.`)} data-testid={`country-whatsapp-${c.slug}`} className="mt-4 btn-pill-inv w-full justify-center">
                WhatsApp <ArrowUpRight size={14}/>
              </a>
              <Link to="/contact" data-testid={`country-form-${c.slug}`} className="mt-3 btn-pill w-full justify-center">
                Or send the form
              </Link>
              <div className="mt-6 pt-6 border-t border-black/10">
                <div className="kbd">Cities we hear from</div>
                <ul className="mt-2 space-y-1 text-sm text-ink-muted">
                  {c.cities.map((city) => <li key={city}>· {city}</li>)}
                </ul>
              </div>
            </div>
          </FadeUp>
        </div>

        {/* Logistics */}
        <div className="mt-32">
          <FadeUp>
            <div className="kbd">— The travel piece</div>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tightest leading-[1.02]">
              What travel from {c.name} looks like.
            </h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {Object.entries(c.logistics).map(([k, v], i) => {
              const Icon = LOGISTIC_ICONS[k] || Plane;
              return (
                <FadeUp key={k} delay={i * 0.05}>
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-2">
                      <Icon size={22} className="text-crimson" strokeWidth={1.5}/>
                    </div>
                    <div className="col-span-10">
                      <div className="kbd">{k}</div>
                      <p className="mt-2 text-ink leading-relaxed">{v}</p>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>

        {/* Local note */}
        <FadeUp className="mt-24">
          <div className="hairline p-6 md:p-10 bg-white/60">
            <div className="kbd">— Local note</div>
            <p className="mt-4 text-lg md:text-xl leading-relaxed max-w-3xl">{c.localNote}</p>
          </div>
        </FadeUp>

        {/* Common conditions */}
        {commonConds.length > 0 && (
          <div className="mt-32">
            <FadeUp>
              <div className="grid grid-cols-12 gap-6 items-end mb-14">
                <div className="col-span-12 md:col-span-8">
                  <div className="kbd">— What patients from {c.name} ask about most</div>
                  <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tightest leading-[1.02]">Common referrals</h2>
                </div>
                <div className="col-span-12 md:col-span-4 md:text-right">
                  <Link to="/care" className="kbd hover:text-ink">All conditions →</Link>
                </div>
              </div>
            </FadeUp>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {commonConds.map((cond) => (
                <Link key={cond.slug} to={`/conditions/${cond.slug}`} data-testid={`country-condition-${cond.slug}`} className="group hairline p-5 bg-white/60 hover:bg-white transition-colors">
                  <div className="kbd">Condition</div>
                  <h3 className="mt-2 font-serif text-2xl tracking-tightest group-hover:text-crimson transition-colors">{cond.name}</h3>
                  <p className="mt-2 text-sm text-ink-muted leading-relaxed">{cond.oneLiner}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Story */}
        {c.story && (
          <FadeUp className="mt-32">
            <div className="hairline-t pt-16">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-2 kbd">— Story</div>
                <div className="col-span-12 md:col-span-10">
                  <blockquote className="font-serif italic text-3xl md:text-5xl leading-[1.15] tracking-tightest max-w-4xl">
                    "{c.story.quote}"
                  </blockquote>
                  <div className="mt-6 kbd">— {c.story.attribution}</div>
                  <p className="mt-4 text-xs text-ink-muted">Anonymised with consent. Individual outcomes vary.</p>
                </div>
              </div>
            </div>
          </FadeUp>
        )}

        {/* Other countries */}
        <div className="mt-32">
          <FadeUp>
            <div className="hairline-t pt-12">
              <div className="kbd">— Other countries</div>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                {others.map((o) => (
                  <Link key={o.slug} to={`/nri/${o.slug}`} className="group hairline p-4 bg-white/60 hover:bg-white transition-colors">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl">{o.flag}</span>
                      <ArrowUpRight size={14} className="text-ink-muted group-hover:text-crimson"/>
                    </div>
                    <div className="mt-4 font-serif text-lg tracking-tightest group-hover:text-crimson">{o.name}</div>
                  </Link>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>

        {/* CTA */}
        <FadeUp className="mt-24">
          <div className="hairline-t pt-12 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-8">
              <h3 className="font-serif text-4xl md:text-6xl tracking-tightest leading-[1.02] max-w-3xl">
                Ready to start from {c.name}?
              </h3>
              <p className="mt-4 text-ink-muted max-w-xl">The team will schedule the first video consult in a slot that suits your timezone.</p>
            </div>
            <div className="col-span-12 md:col-span-4 md:pl-8 self-end flex flex-wrap gap-3">
              <Link to="/contact" className="btn-pill-inv">Book consult <ArrowUpRight size={14}/></Link>
              <a target="_blank" rel="noreferrer" href={WHATSAPP_LINK(`Hello Dr Sidhu, writing from ${c.name}.`)} className="btn-pill">WhatsApp</a>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
